import type { SignupBaselineContent, SignupBaselineWording } from '@galapagos/shared/registration';
import type { CatalogWords } from '../services/catalog-words';

/** One equivalent in the "what counts as one" guide, with its words. */
export interface BaselineEquivalent {
  /** A design-system icon name. */
  icon: string;
  label: string;
  sublabel?: string;
}

/** The "what counts as one" guide, with its words. */
export type BaselineUnitGuide =
  | { kind: 'equivalents'; title: string; items: BaselineEquivalent[] }
  | { kind: 'text'; title: string; body: string };

/** What the last-week steps say about the member's substance, in the language on screen. */
export interface BaselineWording {
  /** The last-week title, when the program has its own (a yes/no question); else the step's own. */
  weekTitle?: string;
  /** Under the last-week title: what to count each day. */
  weekDescription: string;
  unitGuide?: BaselineUnitGuide;
  /** Beside the week's total on the summary. */
  totalLabel: string;
  /** Beside the count of days with none on the summary. */
  freeLabel: string;
  /** The unit a day's count is read out in. */
  unit: { one: string; other: string };
}

const WORDING = 'baseline.wording';

/**
 * The wording for the member's primary program, with the catalog key its
 * words sit under: the first program in the content's priority that they
 * chose decides, and a program with no wording of its own, or no choice at
 * all, reads the default. Values in the selection that are not program keys
 * (a sub-type) never match the priority.
 */
export function pickBaselineWording(
  content: SignupBaselineContent,
  programSelection: readonly string[] | null,
): { key: string; wording: SignupBaselineWording } {
  const chosen = new Set(programSelection ?? []);
  const primary = content.priority.find((programKey) => chosen.has(programKey));
  const own = primary === undefined ? undefined : content.wordings[primary];
  if (primary !== undefined && own !== undefined) {
    return { key: `${WORDING}.programs.${primary}`, wording: own };
  }
  return { key: `${WORDING}.default`, wording: content.default };
}

/**
 * The picked wording with its words from the signup catalog. The guide
 * follows the served guide's kind, and its items are keyed by icon; an item
 * the catalog holds no sublabel for has none.
 */
export function wordedBaseline(
  words: CatalogWords,
  content: SignupBaselineContent,
  programSelection: readonly string[] | null,
): BaselineWording {
  const { key, wording } = pickBaselineWording(content, programSelection);
  const text = (path: string) => words.text(`${key}.${path}`);
  const guide = wording.unitGuide;
  const unitGuide: BaselineUnitGuide | undefined =
    guide === undefined
      ? undefined
      : guide.kind === 'text'
        ? { kind: 'text', title: text('unitGuide.title'), body: text('unitGuide.body') }
        : {
            kind: 'equivalents',
            title: text('unitGuide.title'),
            items: guide.items.map(({ icon }) => {
              const sublabel = `${key}.unitGuide.items.${icon}.sublabel`;
              return {
                icon,
                label: text(`unitGuide.items.${icon}.label`),
                ...(words.has(sublabel) ? { sublabel: words.text(sublabel) } : {}),
              };
            }),
          };
  const weekTitle = `${key}.weekTitle`;
  return {
    ...(words.has(weekTitle) ? { weekTitle: words.text(weekTitle) } : {}),
    weekDescription: text('weekDescription'),
    ...(unitGuide === undefined ? {} : { unitGuide }),
    totalLabel: text('totalLabel'),
    freeLabel: text('freeLabel'),
    unit: { one: text('unit.one'), other: text('unit.other') },
  };
}
